// TypeScript mirror of the Go probe-frame codec in internal/protocol/frame.go.
//
// The frame is a fixed 32-byte little-endian header followed by deterministic
// padding up to the requested message size. Both codecs are pinned to the same
// bytes by the shared vectors in internal/protocol/testvectors.json (mirrored
// in protocol.test.ts). Keep this file byte-for-byte compatible with the Go
// implementation.

/** Size of the fixed frame header in bytes. */
export const HEADER_SIZE = 32;

/**
 * Frame magic: the ASCII "PLM1" read as a little-endian u32 (0x504C4D31 ===
 * 1347177777). On the wire the first byte is 0x31 ('1').
 */
export const MAGIC = 0x504c4d31;

/** Decoded probe-frame header. Timestamps are microseconds as float64. */
export interface Header {
  /** Random per run; lets a node discard stray frames. */
  runId: number;
  /** 0..n-1 per (run, node). */
  seq: number;
  /** Client performance.now() microseconds. */
  tClientSend: number;
  /** 0 from client; node stamps its monotonic microseconds on echo. */
  tServerRecv: number;
  /** 0 from client; node stamps count-received-so-far on echo. */
  serverSeq: number;
}

// Field offsets within the header, in bytes.
const OFF_MAGIC = 0;
const OFF_RUN_ID = 4;
const OFF_SEQ = 8;
const OFF_T_CLIENT_SEND = 12;
const OFF_T_SERVER_RECV = 20;
const OFF_SERVER_SEQ = 28;

/**
 * Deterministic padding value for the i-th padding byte (0-based from offset
 * HEADER_SIZE): i mod 256. Matches protocol.PaddingByte in Go.
 */
export function paddingByte(i: number): number {
  return i & 0xff;
}

/**
 * Serialise a header into a new Uint8Array of exactly `size` bytes (clamped up
 * to HEADER_SIZE). The magic is always written as MAGIC; bytes from HEADER_SIZE
 * onward are filled with deterministic padding.
 */
export function encode(header: Header, size: number): Uint8Array<ArrayBuffer> {
  const n = size < HEADER_SIZE ? HEADER_SIZE : size;
  const buf = new Uint8Array(n);
  const view = new DataView(buf.buffer);
  view.setUint32(OFF_MAGIC, MAGIC, true);
  view.setUint32(OFF_RUN_ID, header.runId >>> 0, true);
  view.setUint32(OFF_SEQ, header.seq >>> 0, true);
  view.setFloat64(OFF_T_CLIENT_SEND, header.tClientSend, true);
  view.setFloat64(OFF_T_SERVER_RECV, header.tServerRecv, true);
  view.setUint32(OFF_SERVER_SEQ, header.serverSeq >>> 0, true);
  for (let i = HEADER_SIZE; i < n; i++) {
    buf[i] = paddingByte(i - HEADER_SIZE);
  }
  return buf;
}

/**
 * Parse a header from a frame. Accepts either an ArrayBuffer or a Uint8Array
 * (including a subarray view). Throws if the input is shorter than the header
 * or the magic does not match.
 */
export function decode(input: ArrayBufferLike | Uint8Array): Header {
  const buf = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (buf.byteLength < HEADER_SIZE) {
    throw new Error('protocol: frame shorter than header');
  }
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const magic = view.getUint32(OFF_MAGIC, true);
  if (magic !== MAGIC) {
    throw new Error('protocol: bad magic');
  }
  return {
    runId: view.getUint32(OFF_RUN_ID, true),
    seq: view.getUint32(OFF_SEQ, true),
    tClientSend: view.getFloat64(OFF_T_CLIENT_SEND, true),
    tServerRecv: view.getFloat64(OFF_T_SERVER_RECV, true),
    serverSeq: view.getUint32(OFF_SERVER_SEQ, true),
  };
}
